import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { storyImg } from "@/components/ImpactStories";
import { Plus, Pencil, Trash2, Eye, EyeOff, Upload } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { kind: "beneficiary", name: "", role: "", place: "", title: "", story: "", quote: "", impact: "", cta: "support", consent: false, published: false, order: 0 };
const KINDS = ["sponsor", "vendor", "beneficiary", "partner"];
const CTAS = [["support", "Support a Family"], ["vendor", "Become a TMF Vendor"], ["sponsor", "Partner / Sponsor TMF"], ["none", "No button"]];
const Sel = ({ value, onChange, opts, id }) => (
  <select data-testid={id} value={value} onChange={(e) => onChange(e.target.value)} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm capitalize">
    {opts.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
  </select>
);

function StoryForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [id, setId] = useState(initial.id);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    if (f.published && !f.consent) { toast.error("Confirm the participant's consent before publishing."); return; }
    if (f.published && !file && !f.image_url) { toast.error("Add a photo before publishing this story."); return; }
    setBusy(true);
    try {
      const body = { ...f, order: Number(f.order) || 0 };
      const wantPublish = body.published;
      let s = id ? (await api.put(`/admin/impact-stories/${id}`, file ? { ...body, published: false } : body)).data.story
        : (await api.post("/admin/impact-stories", { ...body, published: false })).data.story;
      setId(s.id);
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/admin/impact-stories/${s.id}/image`, fd);
      }
      if (wantPublish && (!id || file)) s = (await api.put(`/admin/impact-stories/${s.id}`, body)).data.story;
      toast.success(wantPublish ? "Story published to the homepage" : "Story saved as draft");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); onSaved(false); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="story-form-dialog">
        <DialogHeader><DialogTitle>{initial.id ? "Edit impact story" : "New impact story"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <Sel id="story-kind" value={f.kind} onChange={set("kind")} opts={KINDS.map((k) => [k, k])} />
          <Sel id="story-cta" value={f.cta} onChange={set("cta")} opts={CTAS} />
          <Input data-testid="story-name" placeholder="Participant name" value={f.name} onChange={set("name")} />
          <Input data-testid="story-role" placeholder="Role (e.g. TMF Food Vendor)" value={f.role} onChange={set("role")} />
          <Input data-testid="story-place" placeholder="Location (e.g. Yola, Adamawa State)" value={f.place} onChange={set("place")} />
          <Input data-testid="story-order" type="number" placeholder="Display order" value={f.order} onChange={set("order")} />
        </div>
        <Input data-testid="story-title" placeholder="Headline" value={f.title} onChange={set("title")} />
        <Textarea data-testid="story-story" rows={4} placeholder="Short story" value={f.story} onChange={set("story")} />
        <Textarea data-testid="story-quote" rows={3} placeholder="Testimonial quote" value={f.quote} onChange={set("quote")} />
        <Input data-testid="story-impact" placeholder="Verified impact indicator (optional), e.g. 120 families supported" value={f.impact} onChange={set("impact")} />
        <label className="flex items-center gap-2 text-sm"><Upload className="w-4 h-4" />
          <input data-testid="story-photo" type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} className="text-sm" />
        </label>
        {!file && f.image_url && <img src={storyImg(f.image_url)} alt="" className="h-28 rounded-lg object-cover" />}
        <label className="flex items-start gap-2 text-sm"><Checkbox data-testid="story-consent" checked={f.consent} onCheckedChange={(v) => setF({ ...f, consent: !!v })} />I confirm the participant has given consent to publish their name, photo and story.</label>
        <label className="flex items-center gap-2 text-sm"><Checkbox data-testid="story-published" checked={f.published} onCheckedChange={(v) => setF({ ...f, published: !!v })} />Publish on the homepage</label>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="story-save-btn" disabled={busy} onClick={save} className="bg-emerald-700 hover:bg-emerald-800">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const AdminImpactStories = () => {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => { api.get("/admin/impact-stories").then(({ data }) => setRows(data.stories)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (s) => {
    try { await api.put(`/admin/impact-stories/${s.id}`, { ...s, published: !s.published }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete the story of ${s.name}?`)) return;
    try { await api.delete(`/admin/impact-stories/${s.id}`); toast.success("Story deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const review = async (s, action) => {
    const note = action === "reject" ? window.prompt("Reason (shown to the submitter):", "") : "";
    if (note === null) return;
    try { await api.post(`/admin/impact-stories/${s.id}/review`, { action, note }); toast.success(action === "approve" ? "Story approved and published" : "Story rejected"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const pending = rows.filter((s) => s.review_status === "pending");
  return (
    <div className="mt-10" data-testid="admin-impact-stories">
      {pending.length > 0 && (
        <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="story-review-queue">
          <h3 className="font-semibold text-amber-900">{pending.length} stor{pending.length > 1 ? "ies" : "y"} submitted for review</h3>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {pending.map((s) => (
              <div key={s.id} className="rounded-xl bg-white border border-amber-100 p-3 flex gap-3" data-testid={`review-story-${s.id}`}>
                {s.image_url && <img src={storyImg(s.image_url)} alt="" className="w-24 h-24 rounded-lg object-cover shrink-0" />}
                <div className="min-w-0 flex-1 text-sm">
                  <b>{s.title}</b>
                  <p className="text-xs text-gray-500">{s.name} · <span className="capitalize">{s.kind}</span> · {s.place} · from {s.submitter_label} {s.submitter_phone}</p>
                  <p className="text-xs text-gray-700 mt-1 line-clamp-3">{s.story}</p>
                  <div className="mt-2 flex gap-1">
                    <Button size="sm" className="h-7 bg-emerald-700 hover:bg-emerald-800" onClick={() => review(s, "approve")} data-testid={`story-approve-${s.id}`}>Approve & publish</Button>
                    <Button size="sm" variant="outline" className="h-7" onClick={() => setEdit(s)} data-testid={`story-review-edit-${s.id}`}>Edit</Button>
                    <Button size="sm" variant="outline" className="h-7 text-red-600" onClick={() => review(s, "reject")} data-testid={`story-reject-${s.id}`}>Reject</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-xl text-emerald-950">Impact Stories (homepage)</h2>
        <Button data-testid="story-add-btn" onClick={() => setEdit(EMPTY)} className="rounded-full bg-emerald-700 hover:bg-emerald-800"><Plus className="w-4 h-4 mr-1" />Add story</Button>
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {rows.filter((s) => s.review_status !== "pending").map((s) => (
          <div key={s.id} className="rounded-2xl border border-emerald-100 bg-white p-4 flex gap-3" data-testid={`admin-story-${s.id}`}>
            {s.image_url ? <img src={storyImg(s.image_url)} alt="" className="w-20 h-20 rounded-xl object-cover shrink-0" /> : <div className="w-20 h-20 rounded-xl bg-gray-100 shrink-0" />}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2"><b className="text-sm truncate">{s.name}</b><Badge className={s.published ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-600"}>{s.published ? "Published" : s.review_status === "rejected" ? "Rejected" : "Draft"}</Badge></div>
              <p className="text-xs text-gray-500 truncate capitalize">{s.kind} · {s.place}</p>
              <p className="text-xs text-gray-700 line-clamp-2 mt-1">{s.title}</p>
              <div className="mt-2 flex gap-1">
                <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => setEdit(s)} data-testid={`story-edit-${s.id}`}><Pencil className="w-3 h-3" /></Button>
                <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => toggle(s)} data-testid={`story-toggle-${s.id}`}>{s.published ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}</Button>
                <Button size="sm" variant="outline" className="h-7 px-2 text-red-600" onClick={() => remove(s)} data-testid={`story-delete-${s.id}`}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {edit && <StoryForm initial={edit} onClose={() => setEdit(null)} onSaved={(ok = true) => { load(); if (ok) setEdit(null); }} />}
    </div>
  );
};
